import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";

import Button from "../button/Button";
import Svg from "../svg/Svg";
import { Error, Info, Warning, Question } from "../svg/svgImageList";
import { translationService } from "../../services/translation/TranslationService";
import "./DataGridCell.scss";
import { DisplayValues } from "./interfaces";
import { IssueSeverity } from "../../generated/SewerObjects";

interface ICell {
  column: string;
  item: DisplayValues;
  cellIsClickable?: boolean;
}

export default observer(function DataGridCell(props: ICell) {
  const { item, column, cellIsClickable } = {
    ...props,
  };

  const itemDisplayValues = useMemo(() => {
    return (item.displayValues as any);
  }, [item.displayValues]);

  const getCellClassName = useCallback(
    (cellPath: string | undefined, cellDataIsDeleted: boolean): string | undefined => {
      if (itemDisplayValues[column]?.className)
        return `table-cell-content ${itemDisplayValues[column].className}`;

      return cellIsClickable && cellPath && !cellDataIsDeleted
        ? "table-cell-content clickable"
        : "table-cell-content";
    },
    [cellIsClickable, column, itemDisplayValues]
  );

  const getCellContent = useCallback(
    (
      cellPath: string | undefined,
      cellValue: any,
      cellDataIsDeleted: boolean,
      cellDataHasIssues: boolean,
      cellMaxIssueSeverity: IssueSeverity,
      cellMissingGeometry: boolean,
      cellEntityName: string,
      cellChild?: any
    ) => {
      return (
        <>
          <span className={getCellClassName(cellPath, cellDataIsDeleted)}>
            {cellChild && <div className="cell-child">{cellChild()}</div>}
            <span title={cellValue} className="cell-text">
              {cellValue || cellValue === 0 ? cellValue : " — "}
            </span>
            {cellDataHasIssues && cellMaxIssueSeverity === IssueSeverity.Error && (
              <Svg
                className="issues error"
                svg={Error}
                title={translationService.translate("validation.issues-error", {
                  entity: translationService.translate(cellEntityName),
                })}
              />
            )}
            {cellDataHasIssues && cellMaxIssueSeverity === IssueSeverity.Warning && (
              <Svg
                className="issues warning"
                svg={Warning}
                title={translationService.translate("validation.issues-warning", {
                  entity: translationService.translate(cellEntityName),
                })}
              />
            )}
            {cellDataHasIssues && cellMaxIssueSeverity === IssueSeverity.Info && (
              <Svg
                className="issues info"
                svg={Info}
                title={translationService.translate("validation.issues-info", {
                  entity: translationService.translate(cellEntityName),
                })}
              />
            )}
            {cellMissingGeometry && (
              <Svg
                className="issues missing-geometry"
                svg={Question}
                title={translationService.translate("validation.issues-missing-geometry")}
              />
            )}
          </span>
        </>
      );
    },
    [getCellClassName]
  );

  const getContentAsClickableCell = useCallback(
    (cellPath: string, column: string, content: JSX.Element, absolute?: boolean) => {
      const itemPathWithId = cellPath.replace(":id", String(itemDisplayValues[column].id));
      if (absolute) {
        return (
          <a href={cellPath}>{content}</a>
        )
      }
      return (
        <Link className="trimmed-text" to={itemPathWithId}>
          {content}
        </Link>
      );
    },
    [itemDisplayValues]
  );

  const getContentAsClickableButtonCell = useCallback(
    (onClick: any, column: string) => {
      return (
        <Button
          className={"table-cell-content clickable"}
          onClick={() => onClick()}
          textKey={itemDisplayValues[column].toDisplay}
        />
      );
    },
    [itemDisplayValues]
  );

  const cellContent = useCallback(
    (column: string) => {
      const cellValue = itemDisplayValues[column]?.toDisplay;
      const cellPath = itemDisplayValues[column]?.path;
      const absolutePath = itemDisplayValues[column]?.absolute;
      const onClick = itemDisplayValues[column]?.onClick;
      const cellDataIsDeleted = !!itemDisplayValues[column]?.isDeleted;
      const cellDataHasIssues = !!itemDisplayValues[column]?.hasIssues;
      const cellMaxIssueSeverity = cellDataHasIssues
        ? itemDisplayValues[column]?.maxIssueSeverity
        : undefined;
      const cellMissingGeometry = !!itemDisplayValues[column]?.hasMissingGeometry;
      const cellEntityName = cellDataHasIssues ? itemDisplayValues[column]?.entityName : "";
      const cellChild = itemDisplayValues[column]?.child;

      let content = getCellContent(
        cellPath,
        cellValue,
        cellDataIsDeleted,
        cellDataHasIssues,
        cellMaxIssueSeverity!,
        cellMissingGeometry,
        cellEntityName!,
        cellChild
      );

      if (cellDataIsDeleted) return content;

      if (cellIsClickable && cellPath) {
        content = getContentAsClickableCell(cellPath, column, content, absolutePath);
      } else if (cellIsClickable && onClick) {
        content = getContentAsClickableButtonCell(onClick, column);
      }

      return content;
    },
    [
      itemDisplayValues,
      getCellContent,
      cellIsClickable,
      getContentAsClickableCell,
      getContentAsClickableButtonCell,
    ]
  );

  return <>{cellContent(column)}</>;
});
